import { z } from "zod";

export const HibachiConnectionStatus = {
  ONLINE: "online",
} as const;
export type HibachiConnectionStatus = (typeof HibachiConnectionStatus)[keyof typeof HibachiConnectionStatus];

export const AddHibachiAccountSchema = z.object({
  accountId: z.number().int().positive(),
  walletAddress: z.string().min(1),
  status: z.string(),
});
export type AddHibachiAccountDto = z.infer<typeof AddHibachiAccountSchema>;

/** EIP-191 `personal_sign` output, hex, no 0x prefix. */
const SignatureHex = z.string().regex(/^[a-fA-F0-9]{130}$/, "signature must be 130 hex chars (no 0x prefix)");
const UnixSeconds = z.string().regex(/^\d+$/, "timestamp must be unix seconds as string");

/**
 * Request body for `POST /v1/exchange-account/hibachi/connect-wallet`.
 *
 * Opens the connection. Carries only the signatures needed to log in and to
 * re-derive the trading key — NOT the key-generation grants. Hibachi burns a
 * signature on first use and rejects it as `Signature has already been used.`
 * thereafter, so every grant is signed just-in-time and posted separately to
 * `/connect-wallet/grant`.
 */
export const ConnectHibachiFromWalletSchema = z.object({
  walletAddress: z.string().regex(/^0x[a-fA-F0-9]{40}$/, "walletAddress must be a 0x-prefixed 20-byte hex"),
  /** Signs `Hibachi Verification at <authTimestamp>`; spent on `/auth/wallet-auth`. */
  authSignature: SignatureHex,
  authTimestamp: UnixSeconds,
  /**
   * Signature of the literal message "Hibachi Trading Verification" (no
   * timestamp, deterministic). The backend uses the first 16 bytes as BIP39
   * entropy to reproduce Hibachi's client-side ECDSA trading key derivation.
   * Deterministic, so it carries no replay risk and is reused across steps.
   */
  derivationSignature: SignatureHex,
  /**
   * Optional user-chosen name (max 32 chars). Used for both the Cede
   * exchange-account label and the Hibachi API key alias so they stay in
   * sync. When omitted, the backend falls back to `Hibachi ..{last4(wallet)}`.
   */
  name: z.string().trim().min(1).max(32).optional(),
});
export type ConnectHibachiFromWalletDto = z.infer<typeof ConnectHibachiFromWalletSchema>;

/**
 * Body of `POST /v1/exchange-account/hibachi/connect-wallet/grant`.
 *
 * Called once per API key left to mint, in the order the backend plans them
 * (primary account first, then each new funded sub-account). Carries ONE fresh
 * `Hibachi Verification at <timestamp>` signature, spent on a single
 * `/auth/wallet-auth/verify` to arm one `generate_key` grant.
 *
 * We DON'T collect every grant in one shot because Hibachi burns a signature on
 * first use (`Signature has already been used.`) and rejects a timestamp older
 * than ~60s. Batching would age the first signature past the window while the
 * user is still approving the last one. So the client signs immediately before
 * each call, mirroring how hibachi.xyz re-signs per grant.
 *
 * Steps are tracked backend-side against `prepareId` rather than indexed by the
 * client: a grant is single-use and non-idempotent, so a client-supplied index
 * could only ever disagree with the server's cursor.
 */
export const ConnectHibachiGrantBodySchema = z.object({
  prepareId: z.uuid(),
  signature: SignatureHex,
  timestamp: UnixSeconds,
});
export type ConnectHibachiGrantBodyDto = z.infer<typeof ConnectHibachiGrantBodySchema>;

/**
 * Response from both `/connect-wallet` and `/connect-wallet/grant`.
 * Discriminated on `isFinal`:
 *   - `false` — `remainingGrants` API keys still need minting; the client signs
 *     a fresh message and posts it to `/connect-wallet/grant`.
 *   - `true`  — every key minted and persisted; account fields populated.
 */
export const ConnectHibachiResultSchema = z.discriminatedUnion("isFinal", [
  z.object({
    isFinal: z.literal(false),
    prepareId: z.uuid(),
    remainingGrants: z.number().int().positive(),
  }),
  z.object({
    isFinal: z.literal(true),
    accountId: z.number().int().positive(),
    walletAddress: z.string(),
    status: z.string(),
  }),
]);
export type ConnectHibachiResultDto = z.infer<typeof ConnectHibachiResultSchema>;

/**
 * Request body for `POST /v1/exchange-account/hibachi/connect-wallet/totp`.
 *
 * Submitted after the initial /connect-wallet call returned `HIBACHI_TOTP_REQUIRED`.
 * `prepareId` references the cached wallet-auth session (cookies + derived
 * keys) on the backend; `token` is the 6-digit code from the user's
 * authenticator app.
 */
export const ConnectHibachiTotpSchema = z.object({
  prepareId: z.uuid(),
  token: z.string().regex(/^\d{6}$/, "token must be a 6-digit authenticator code"),
});
export type ConnectHibachiTotpDto = z.infer<typeof ConnectHibachiTotpSchema>;
